<template>
  <v-container fluid>
    <v-form ref="form" v-model="form" @submit.prevent="submit" :class="{ 'form-disabled': isFormDisabled }">
      <v-row>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Job Scope*"
            placeholder="Select a job scope"
            :rules="[rules.required]"
            outlined
            dense
            v-model="operationBaseFieldsForm.job_scope"
            prepend-inner-icon="mdi-script"
            :items="jobScopes"
            @change="onSectorChange(operationBaseFieldsForm)"
          ></v-select>
        </v-col>
        <v-col v-if="operationBaseFieldsForm.job_scope === 'OPL(outside port limits)'" cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="OPL Sub Sectors"
            placeholder="Filter by OPL Sub Sectors"
            outlined
            dense
            :items="loadOPLSubSectors(operationBaseFieldsForm.job_scope)"
            v-model="operationBaseFieldsForm.sub_sector"
            prepend-inner-icon="mdi-anchor"
            clearable
          ></v-select>
        </v-col>
        <v-col v-bind="vesselColumnProps" class="pb-0">
          <v-select
            label="Vessel*"
            placeholder="Select a vessel"
            :rules="[rules.required]"
            outlined
            dense
            v-model="operationBaseFieldsForm.vessel_id"
            prepend-inner-icon="mdi-ferry"
            :items="vessels"
            item-text="name"
            item-value="id"
            @change="setCustomer"
          >
            <template v-slot:prepend-item>
              <v-text-field
                v-model="vesselSearchQuery"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search vessels..."
                clearable
                class="px-4"
                @input="filterVessels"
              ></v-text-field>
            </template>
          </v-select>
        </v-col>
        <v-col v-if="!operation.id && !['operations', 'accounts'].includes($store.state.auth.profile.role)" cols="1" sm="1" md="1" lg="1" class="pb-0">
          <v-btn icon @click="dialogForm = {action: 'Create Vessel', showForm: true, component: 'VesselApiForm', vessel: {}}">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Principal*"
            placeholder="Select a principal"
            outlined
            :rules="[rules.required]"
            dense
            v-model="operationBaseFieldsForm.card_id"
            prepend-inner-icon="mdi-account"
            :items="owners"
            item-text="name"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-menu
            v-model="jobStartDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="computedJobStartDateFormatted"
                label="Job Start Date*"
                dense
                :rules="[rules.required]"
                outlined
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="operationBaseFieldsForm.job_start_date"
              no-title
              @input="jobStartDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-datetime-picker
            ref="eta"
            :label="etaLabel"
            v-model="operationBaseFieldsForm.eta"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, rules: etaRules, id: 'etaField'}"
            @input="checkIfJobOverlap"
            >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>                                                         
          </v-datetime-picker>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-datetime-picker
            ref="etd"
            :label="etdLabel"
            v-model="operationBaseFieldsForm.etd"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, rules: etdRules, id: 'etdField'}"
            @input="checkIfJobOverlap"
            >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>                                                         
          </v-datetime-picker>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select v-if="['admin', 'manager'].includes($store.state.auth.profile.role)"
            label="Incharge*"
            placeholder="Select Incharge"
            :rules="[rules.required]"
            outlined
            dense
            v-model="operationBaseFieldsForm.supervisor_id"
            prepend-inner-icon="mdi-account"
            :items="users"
            item-text="name"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-datetime-picker
            ref="arrivalBearth"
            label="Arrival Bearth"
            v-model="operationBaseFieldsForm.arrival_bearth"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, id: 'arrivalBearthField'}"
          >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Bearthing Station"
            placeholder="Enter Bearthing Station"
            outlined
            dense
            v-model="operationBaseFieldsForm.bearthing_station"
            prepend-inner-icon="mdi-fullscreen"
            clearable
          ></v-text-field>
        </v-col>
        <v-col v-if="['Billing', 'Completed'].includes(operationBaseFieldsForm.status) && $root.onlyAccountsCanAccess" cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Billing Party*"
            placeholder="Select Billing Party"
            outlined
            dense
            v-model="operationBaseFieldsForm.billing_party_id"
            prepend-inner-icon="mdi-account"
            :rules="[rules.required]"
            :disabled="billingParties.length === 0"
            :hint="billingParties.length === 0 ? 'Loading billing parties...' : ''"
            :persistent-hint="billingParties.length === 0"
            :items="billingParties"
            item-text="name"
            item-value="id"
            :class="operationBaseFieldsForm.status === 'Billing' ? 'billing-party-field' : ''"
          ></v-select>
        </v-col>
        <v-col v-if="['Billing', 'Completed'].includes(operationBaseFieldsForm.status) && $root.onlyAccountsCanAccess" cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="PO Number"
            placeholder="Enter PO Number"
            outlined
            dense
            clearable
            v-model="operationBaseFieldsForm.po_number"
            prepend-inner-icon="mdi-counter"
            :class="operationBaseFieldsForm.status === 'Billing' ? 'po-number-field' : ''"
          ></v-text-field>
        </v-col>
        <v-col cols="12" class="pb-0">
          <v-textarea
            label="Arrival Remarks"
            :rows="2"
            placeholder="Enter Arrival Remarks"
            outlined
            dense
            v-model="operationBaseFieldsForm.remarks_arrival"
          ></v-textarea>
        </v-col>
        <v-col cols="12" class="pb-0">
          <v-textarea
            label="Departure Remarks"
            :rows="2"
            placeholder="Enter Departure Remarks"
            outlined
            dense
            v-model="operationBaseFieldsForm.remarks_departure"
          ></v-textarea>
        </v-col>
      </v-row>
      <v-row v-if="!isDraft">
        <v-col cols="12">
          <v-switch
            dense
            hide-details
            v-model="operationBaseFieldsForm.vessel_information"
            label="Include Vessel Arrival - Departure Information?"
            class="mt-0 pt-0"
          ></v-switch>
        </v-col>
      </v-row>
      <v-row v-if="operationBaseFieldsForm.vessel_information">
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Captain Name"
            placeholder="Enter Captain Name"
            outlined
            dense
            v-model="vesselInfoForm.captain_name"
            prepend-inner-icon="mdi-account"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Captain Email Address"
            placeholder="Enter Captain Email"
            outlined
            dense
            v-model="vesselInfoForm.captain_mail_id"
            prepend-inner-icon="mdi-gmail"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Vessel Contact"
            placeholder="Enter Vessel Contact"
            outlined
            dense
            v-model="vesselInfoForm.vessel_contact"
            prepend-inner-icon="mdi-phone"
            clearable
          ></v-text-field>
        </v-col>
        <v-col v-if="false" cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-btn color="primary" outlined>
            Send Mail To Captain
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Last Port"
            placeholder="Enter Last Port"
            outlined
            dense
            v-model="vesselInfoForm.last_port"
            prepend-inner-icon="mdi-arrow-bottom-right"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Next Port"
            placeholder="Enter Next Port"
            outlined
            dense
            v-model="vesselInfoForm.next_port"
            prepend-inner-icon="mdi-arrow-top-right"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Forward"
            placeholder="Enter FWD"
            type="number"
            outlined
            dense
            v-model="vesselInfoForm.fwd"
            prepend-inner-icon="mdi-format-align-top"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="AFT"
            placeholder="Enter AFT"
            type="number"
            outlined
            dense
            v-model="vesselInfoForm.aft"
            prepend-inner-icon="mdi-format-align-bottom"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Total Height of Ship"
            placeholder="Enter Height"
            type="number"
            outlined
            dense
            v-model="vesselInfoForm.ht"
            prepend-inner-icon="mdi-format-align-middle"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Freeboard"
            placeholder="Enter Freeboard"
            type="number"
            outlined
            dense
            v-model="vesselInfoForm.freeboard"
            prepend-inner-icon="mdi-arrow-expand-up"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Cargo Type"
            placeholder="Enter Cargo Type"
            outlined
            dense
            v-model="vesselInfoForm.cargo_type"
            prepend-inner-icon="mdi-select-all"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label=" Arrival Qty / Vol(Metric Ton)"
            placeholder="Enter Qty / Vol"
            outlined
            type="number"
            dense
            v-model="vesselInfoForm.qty_volume"
            prepend-inner-icon="mdi-select-inverse"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label=" Departure Qty / Vol(Metric Ton)"
            placeholder="Enter Qty / Vol"
            outlined
            type="number"
            dense
            v-model="vesselInfoForm.departure_qty_volume"
            prepend-inner-icon="mdi-select-inverse"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-datetime-picker
            ref="etaNextPort"
            label="ETA Next Port"
            v-model="vesselInfoForm.eta_next_port"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, id: 'etaNextPortField'}"
          >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="12">
          <v-divider></v-divider>
        </v-col>
      </v-row>
      <v-row v-if="!isDraft">
        <v-col cols="12">
          <v-switch
            dense
            hide-details
            v-model="operationBaseFieldsForm.anchorage_information"
            label="Include Anchorage Information?"
            class="mt-0 pt-0"
          ></v-switch>
        </v-col>
      </v-row>
      <v-row v-if="operationBaseFieldsForm.anchorage_information">
        <v-col cols="12" class="pb-0">
          <span class="headline">Anchorage Details</span>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-switch
            dense
            hide-details
            v-model="vesselInfoForm.anchorage_details"
            label="Berthing?"
            class="mt-1"
          ></v-switch>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Pilot Station"
            placeholder="Filter by Pilot Station"
            outlined
            dense
            :items="pilotStation"
            v-model="vesselInfoForm.anchorage.pilot_station"
            prepend-inner-icon="mdi-fullscreen"
            clearable
          ></v-select>
        </v-col>
        <!-- <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Coming From"
            placeholder="Select coming from"
            outlined
            dense
            v-model="vesselInfoForm.anchorage.coming_from"
            prepend-inner-icon="mdi-arrow-bottom-right"
            :items="['East', 'West']"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Going To"
            placeholder="Select going to"
            outlined
            dense
            v-model="vesselInfoForm.anchorage.going_to"
            prepend-inner-icon="mdi-arrow-top-right"
            :items="['East', 'West']"
          ></v-select>
        </v-col> -->
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="!vesselInfoForm.anchorage_details">
          <v-select
            label="Anchorage Sectors"
            placeholder="Filter by sector"
            outlined
            dense
            :items="sectorItems"
            v-model="vesselInfoForm.anchorage.sector"
            prepend-inner-icon="mdi-anchor"
            clearable
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="!vesselInfoForm.anchorage_details">
          <v-select
            label="Anchorage Sub Sectors"
            placeholder="Filter by sub-sectors"
            outlined
            dense
            :items="loadSubSectors"
            v-model="vesselInfoForm.anchorage.subSector"
            prepend-inner-icon="mdi-anchor"
            clearable
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="vesselInfoForm.anchorage_details">
          <v-datetime-picker
            ref="gangwayDown"
            label="Gangway Down"
            v-model="vesselInfoForm.anchorage.gangway_down"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, id: 'gangwayDownField'}"
          >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="vesselInfoForm.anchorage_details">
          <v-datetime-picker
            ref="sailBearth"
            label="UnBearth"
            v-model="vesselInfoForm.anchorage.sail_berth"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, id: 'sailBearthField'}"
          >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="12">
          <v-divider></v-divider>
        </v-col>
        <v-col cols="12" class="pb-0">
          <span class="headline">Arrival Details</span>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Inbound Voyage"
            placeholder="Enter Inbound Voyage"
            outlined
            type="text"
            dense
            v-model="vesselInfoForm.arrival.inbound_voyage"
            prepend-inner-icon="mdi-arrow-bottom-right"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Weather(Fair / Wet)"
            placeholder="Enter Weather Details"
            outlined
            dense
            :items="weatherItems"
            v-model="vesselInfoForm.arrival.weather"
            prepend-inner-icon="mdi-weather-cloudy"
            clearable
          ></v-select>
        </v-col>
        <v-col cols="12" class="pb-0">
          <span class="headline">Departure Details</span>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-text-field
            label="Outbound Voyage"
            placeholder="Enter Outbound Voyage"
            outlined
            type="text"
            dense
            v-model="vesselInfoForm.departure.outbound_voyage"
            prepend-inner-icon="mdi-arrow-top-right"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
          <v-select
            label="Weather(Fair / Wet)"
            placeholder="Enter Weather Details"
            outlined
            dense
            :items="weatherItems"
            v-model="vesselInfoForm.departure.weather"
            prepend-inner-icon="mdi-weather-cloudy"
            clearable
          ></v-select>
        </v-col>
      </v-row>
      <ReportDetails :operation="operation" :principalName="selectedPrincipalName" />
      <v-btn style="background-color: #273978; color: white;" class="mt-7 float-right" outlined @click="formSubmit()"
        :disabled="loading" :loading="loading">
        {{ isFormDisabled ? 'Next' : 'Save & Continue' }}
      </v-btn>
    </v-form>
    <component
      :is="dialogForm.component"
      :action="dialogForm.action"
      :showForm="dialogForm.showForm"
      :owners="vesselOwners"
      :principal="principals"
      @formSubmitted="onVesselFormSubmitted"
      @closeDialog="resetDialog()"
      :vessel="dialogForm.vessel" />
  </v-container>
</template>

<script>
  import rules from '../../../utilities/validator';
  import _ from 'lodash';
  import { ensureSelectedVesselInList } from '../../../utilities/vesselHelpers';
  import ReportDetails from '../ReportDetails';
  import VesselApiForm from '../../vessels/blocks/VesselApiForm';

  export default {
    name: 's-job-details',
    components: {
      ReportDetails,
      VesselApiForm
    },
    props: {
      operation: {
        type: Object,
        default: {},
      },
      isDraft: {
        type: Boolean,
        default: false
      }
    },
    data() {
      let operationBaseFieldsObj = {
        job_scope: 'Full Agent',
        sub_sector: '',
        job_start_date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
        card_id: null,
        vessel_id: null,
        eta: null,
        etd: null,
        is_draft: false,
        supervisor_id: null,
        billing_party_id: null,
        po_number: '',
        remarks_arrival: '',
        remarks_departure: '',
        arrival_bearth: null,
        bearthing_station: '',
        vessel_information: false,
        anchorage_information: false
      };

      let vesselInfoObj = {
        captain_mail_id: '',
        captain_name: '',
        last_port: '',
        next_port: '',
        fwd: '',
        aft: '',
        ht: '',
        freeboard: '',
        cargo_type: '',
        qty_volume: '',
        departure_qty_volume: '',
        eta_next_port: null,
        vessel_contact: '',
        anchorage_details: false,
        anchorage: {
          pilot_station: '',
          // coming_from: '',
          // going_to: '',
          sector: '',
          subSector: '',
          arrival_bearth: null,
          gangway_down: null,
          bearthing_station: null,
          sail_berth: null
        },
        arrival: {
          weather: '',
          inbound_voyage: ''
        },
        departure: {
          weather: '',
          outbound_voyage: ''
        }
      };

      return {
        dialogForm: {
          component: '',
          showForm: false,
          action: '',
        },
        jobScopes: ["Full Agent", "Owner’s Matter", "Charterer", "Cargo Operation", "Handover / takeover", "OPL(outside port limits)", "Shipyard / Dry Dock", "Sheriff Agent", "Crew Service", "Miscellaneous"],
        customers: [],
        bunkerSuppliers: [],
        lubeOilSuppliers: [],
        surveyors: [],
        vessels: [],
        vesselOwners: [],
        principals: [],
        billingParties: [],
        owners: [],
        users: [],
        vesselSearchQuery: '',
        vesselPage: 1,
        vesselHasMore: false,
        vesselSearchTimeout: null,
        jobStartDateMenu: false,
        bunkerFieldsPopulted: false,
        eTDMenu: false,
        eTAMenu: false,
        form: false,
        rules,
        loading: false,
        tmpForm: Object.assign({}, operationBaseFieldsObj),
        operationBaseFieldsForm: Object.assign({}, operationBaseFieldsObj),
        grades: [
          'High',
          'Intermediate',
          'Medium',
          'Low',
          'Very Low Sulphur',
          'Not Indicated'
        ],
        weatherItems: ['Fair','Wet'],
        tmpFormVessel: Object.assign({}, vesselInfoObj),
        vesselInfoForm: Object.assign({}, vesselInfoObj),
        operationInitSeq: 0,
        pilotStation: ['Western Boarding Ground A (PWBGA)','Western Boarding Ground B (PWBGB)','PGBG Gusong Boarding Ground','Southern Boarding Ground (PSBG)','Eastern Boarding Ground A (PEBGA)','Eastern Boarding Ground B (PEBGB)', 'Eastern Boarding Ground C (PEBGC)','East Johor Strait Boarding Ground (PJSB)'],
        sectorItems: ['Eastern Sector','Western Sector','Jurong Sector'],
        subSectorItems: {
          "Eastern Sector": ["Changi Barge Temporary Holding Anchorage", "Changi General Purposes Anchorage", "Man-of-War Anchorage", "Eastern Bunkering A Anchorage", "Eastern Bunkering B Anchorage", "Eastern Petroleum C Anchorage", "Small Craft B Anchorage", "Small Craft A Anchorage", "Eastern Petroleum B Anchorage", "Eastern Special Purposes A Anchorage", "Eastern Bunkering C Anchorage", "Eastern Holding A Anchorage", "Eastern Petroleum A Anchorage", "Eastern Anchorage", "Eastern Holding B Anchorage", "Eastern Holding C Anchorage"],
          "Western Sector": ["Western Quarantine and Immigration Anchorage", "Western Anchorage", "Western Petroleum A Anchorage", "Western Holding Anchorage", "Western Petroleum B Anchorage", "Raffles Reserved Anchorage", "Raffles Petroleum Anchorage", "Selat Pauh Anchorage", "Selat Pauh Petroleum Anchorage"],
          "Jurong Sector": ["Sudong Petroleum Holding Anchorage", "Sudong Explosive Anchorage", "Sudong Special Purpose Anchorage", "Sudong Holding Anchorage", "Very Large Crude Carrier Anchorage"]
        },
      };
    },
    computed: {
      selectedPrincipalName() {
        const selectedId = this.operationBaseFieldsForm?.card_id;
        if (!selectedId) return '';

        const principal = (this.principals || []).find(p => Number(p.id) === Number(selectedId));
        return principal?.name || '';
      },
      computedJobStartDateFormatted: {
        get() {
          return this.formatDate(this.operationBaseFieldsForm.job_start_date);
        },
        set(value) {
          return value;
        }
      },
      loadSubSectors() {
        return this.subSectorItems[this.vesselInfoForm.anchorage.sector] || [];
      },
       vesselColumnProps() {
        return {
          cols: this.operation.id ? 12 : 10,
          sm: this.operation.id ? 6 : 5,
          md: this.operation.id ? 4 : 3,
          lg: this.operation.id ? 4 : 3
        };
      },
      etaLabel() {
        return this.operationBaseFieldsForm.job_scope === 'Miscellaneous' 
          ? 'Estimated Time of Arrival' 
          : 'Estimated Time of Arrival*';
      },
      etdLabel() {
        return this.operationBaseFieldsForm.job_scope === 'Miscellaneous' 
          ? 'Estimated Time of Departure' 
          : 'Estimated Time of Departure*';
      },
      etaRules() {
        return this.operationBaseFieldsForm.job_scope === 'Miscellaneous' 
          ? [] 
          : [this.rules.required];
      },
      etdRules() {
        return this.operationBaseFieldsForm.job_scope === 'Miscellaneous' 
          ? [] 
          : [this.rules.required];
      },
      isFormDisabled() {
        return ['Billing', 'Completed', 'Cancelled'].includes(this.operationBaseFieldsForm.status) && !['admin'].includes(this.$store.state.auth.profile.role);
      }
    },
    methods: {
      onSectorChange(scopes) {
        if (scopes.job_scope !== 'OPL(outside port limits)') {
          scopes.sub_sector = '';
        }
      },
      filterVessels() {
        if (this.vesselSearchTimeout) {
          clearTimeout(this.vesselSearchTimeout);
        }
        this.vesselSearchTimeout = setTimeout(async () => {
          if (!this.vesselSearchQuery || !this.vesselSearchQuery.trim()) {
            this.getVessels();
            return;
          }
          this.vesselPage = 1;
          this.loadVesselsForSearch();
        }, 500);
      },
      loadVesselsForSearch() {
        const params = {
          page: this.vesselPage,
          per_page: 20,
          search: this.vesselSearchQuery.trim()
        };
        
        this.$api.vessels.dropdown(params)
          .then(async (response) => {
            this.vessels = response.vessels;
            this.vesselHasMore = response.pagination.has_more;
            this.vessels = await ensureSelectedVesselInList({
              vesselId: this.operationBaseFieldsForm.vessel_id,
              vesselList: this.vessels,
              existingVessels: this.vessels,
              loadVesselFn: (id) => this.ensureVesselLoaded(id)
            });
          }).catch((err) => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
      loadOPLSubSectors(location) {
        if (location === 'OPL(outside port limits)') {
          return ['EOPL - 104 18E', 'EOPL - 104 21E', 'HORSBURG - 104 18E', 'HORSBURG - 104 21E', 'South China Sea', 'WOPL - 103 18E'];
        }
        return [];
      },
      onVesselFormSubmitted(newVessel) {
        this.resetDialog();
        if (newVessel && newVessel.id) {
          this.getVessels(newVessel.id);
        }
      },
      resetDialog() {
        this.dialogForm = {showForm: false, action: '', component: ''};
      },
      checkIfJobOverlap() {
        if (this.operationBaseFieldsForm.id || 
            !this.operationBaseFieldsForm.vessel_id || 
            this.operationBaseFieldsForm.job_scope === 'Miscellaneous' ||
            !this.operationBaseFieldsForm.eta || 
            !this.operationBaseFieldsForm.etd) return;
        this.$api.operations.checkVesselOverlap(this.operationBaseFieldsForm.vessel_id, this.operationBaseFieldsForm.eta, this.operationBaseFieldsForm.etd)
          .then((response) => {
            if (response.has_overlap) {
              confirm(`Job already exists #${response.job_name} created by ${response.created_by_name}, do you want to create a new job?`);
            }
          });
      },
      async setCustomer(value) {
        this.checkIfJobOverlap();
        
        const vessel = await this.ensureVesselLoaded(value) ||
                      _.find(this.vessels, {id: value});
        
        if (['Billing', 'Completed'].includes(this.operationBaseFieldsForm.status) && this.$root.onlyAccountsCanAccess) {
          this.billingParties = vessel?.card_id || [];
        }

        // Await owners so card_id (Principal) resolves in the dropdown before render
        try {
          const response = await this.$api.vessel_owners.index(value);
          this.owners = response.owners;
        } catch (err) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
        }

        if(!this.operation.id) {
          if (vessel?.email) {
            this.vesselInfoForm.captain_mail_id = vessel.email;
          }
          if (vessel?.vessel_contact) {
            this.vesselInfoForm.vessel_contact = vessel.vessel_contact;
          }
        }
      },
      sanitizeDateFields() {
        this.$set(this.operationBaseFieldsForm, 'eta', this.parseDateValue(this.operationBaseFieldsForm.eta));
        this.$set(this.operationBaseFieldsForm, 'etd', this.parseDateValue(this.operationBaseFieldsForm.etd));
        this.$set(this.operationBaseFieldsForm, 'arrival_bearth', this.parseDateValue(this.operationBaseFieldsForm.arrival_bearth));
        
        if (this.vesselInfoForm) {
          this.$set(this.vesselInfoForm, 'eta_next_port', this.parseDateValue(this.vesselInfoForm.eta_next_port));
          
          if (this.vesselInfoForm.anchorage) {
            this.$set(this.vesselInfoForm.anchorage, 'arrival_bearth', this.parseDateValue(this.vesselInfoForm.anchorage.arrival_bearth));
            this.$set(this.vesselInfoForm.anchorage, 'gangway_down', this.parseDateValue(this.vesselInfoForm.anchorage.gangway_down));
            this.$set(this.vesselInfoForm.anchorage, 'sail_berth', this.parseDateValue(this.vesselInfoForm.anchorage.sail_berth));
          }
        }
      },
      async submit() {
        this.sanitizeDateFields();
        await this.$nextTick();
        
        if (this.$refs.eta && (this.operationBaseFieldsForm.eta === null || this.operationBaseFieldsForm.eta === undefined)) {
          this.$refs.eta.clearHandler();
        }
        if (this.$refs.etd && (this.operationBaseFieldsForm.etd === null || this.operationBaseFieldsForm.etd === undefined)) {
          this.$refs.etd.clearHandler();
        }
        
        return this.$refs.form.validate();
      },
      async formSubmit() {
        if(!(await this.submit())) return;

        this.sanitizeDateFields();
        await this.$nextTick();
        
        this.loading = true;
        this.operationBaseFieldsForm.is_draft = this.isDraft;
        const cardId = this.operationBaseFieldsForm.card_id;
        
        const operationDetails = JSON.parse(JSON.stringify({ ...this.operationBaseFieldsForm, card_id: cardId }));
        
        if (operationDetails.eta) {
          const etaDate = new Date(operationDetails.eta);
          if (isNaN(etaDate.getTime())) {
            console.error('Invalid ETA date:', operationDetails.eta);
            operationDetails.eta = null;
          } else {
            operationDetails.eta = etaDate.toISOString();
          }
        }
        if (operationDetails.etd) {
          const etdDate = new Date(operationDetails.etd);
          if (isNaN(etdDate.getTime())) {
            console.error('Invalid ETD date:', operationDetails.etd);
            operationDetails.etd = null;
          } else {
            operationDetails.etd = etdDate.toISOString();
          }
        }
        if (operationDetails.arrival_bearth) {
          const arrivalBearthDate = new Date(operationDetails.arrival_bearth);
          operationDetails.arrival_bearth = !isNaN(arrivalBearthDate.getTime()) ? arrivalBearthDate.toISOString() : null;
        }

        const hasPrimaryAnchorageValues = Boolean(
          operationDetails.arrival_bearth || operationDetails.bearthing_station
        );
        
        let vesselInfo = null;
        if (operationDetails.vessel_information || operationDetails.anchorage_information || hasPrimaryAnchorageValues) {
          vesselInfo = JSON.parse(JSON.stringify(this.vesselInfoForm));
          vesselInfo.anchorage = vesselInfo.anchorage || {};
          vesselInfo.anchorage.arrival_bearth = operationDetails.arrival_bearth || vesselInfo.anchorage.arrival_bearth || null;
          vesselInfo.anchorage.bearthing_station = operationDetails.bearthing_station || vesselInfo.anchorage.bearthing_station || null;
          
          if (vesselInfo.eta_next_port) {
            const date = new Date(vesselInfo.eta_next_port);
            vesselInfo.eta_next_port = !isNaN(date.getTime()) ? date.toISOString() : null;
          }
          if (vesselInfo.anchorage?.arrival_bearth) {
            const date = new Date(vesselInfo.anchorage.arrival_bearth);
            vesselInfo.anchorage.arrival_bearth = !isNaN(date.getTime()) ? date.toISOString() : null;
          }
          if (vesselInfo.anchorage?.gangway_down) {
            const date = new Date(vesselInfo.anchorage.gangway_down);
            vesselInfo.anchorage.gangway_down = !isNaN(date.getTime()) ? date.toISOString() : null;
          }
          if (vesselInfo.anchorage?.sail_berth) {
            const date = new Date(vesselInfo.anchorage.sail_berth);
            vesselInfo.anchorage.sail_berth = !isNaN(date.getTime()) ? date.toISOString() : null;
          }
        }
        
        const formData = {
          operation_details: operationDetails,
          vessel_information: vesselInfo
        };
        this.$api.operations.create(formData)
          .then(response => {
            this.operation.id = response.operation.id;
            this.$emit('nextStep', 2, response.operation);
          }).catch(err => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          }).finally(() => (this.loading = false));
      },
      formatDate(date) {
        if (!date) return null

        const [year, month, day] = date.split('-');
        return `${day}-${month}-${year}`;
      },
      async populateDropdown() {
        await Promise.all([
          this.getVessels(),
          this.getUsers(),
          this.populateCards('customers', '')
        ]);
      },
      async getUsers() {
        try {
          const response = await this.$api.users.index();
          this.users = response.users;
        } catch (err) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
        }
      },
      async ensureVesselLoaded(vesselId) {
        if (!vesselId) return null;
        
        const existingVessel = _.find(this.vessels, {id: vesselId}) || 
                              null;
        if (existingVessel) {
          return existingVessel;
        }
        
        try {
          const response = await this.$api.vessels.show(vesselId);
          const vessel = response.vessel;
          
          if (!_.find(this.vessels, {id: vesselId})) {
            this.vessels.push(vessel);
          }
          
          return vessel;
        } catch (err) {
          console.error('Failed to load vessel:', err);
          return null;
        }
      },
      getVessels(newVesselId = null) {
        const params = {
          page: 1,
          per_page: 20
        };
        
        return this.$api.vessels.dropdown(params)
          .then(async (response) => {
            this.vessels = response.vessels;
            
            if (newVesselId) {
              this.operationBaseFieldsForm.vessel_id = newVesselId;
              await this.ensureVesselLoaded(newVesselId);
              this.vessels.sort((a, b) => a.name.localeCompare(b.name));
              this.setCustomer(newVesselId);
            }
            
            const selectedVesselId = this.operationBaseFieldsForm.vessel_id;
            let selectedVessel = null;
            if (selectedVesselId && !newVesselId) {
              selectedVessel = await this.ensureVesselLoaded(selectedVesselId);
            }
            
            if (['Billing', 'Completed'].includes(this.operationBaseFieldsForm.status) && this.$root.onlyAccountsCanAccess) {
              if (!selectedVessel) {
                selectedVessel = _.find(this.vessels, {id: this.operationBaseFieldsForm.vessel_id});
              }
              this.billingParties = selectedVessel?.card_id || [];
            }
            Promise.all([
              this.$api.cards.index('customer,subsidiary'),
              this.$api.cards.index('customer')
            ]).then(([ownersResponse, principalsResponse]) => {
              this.vesselOwners = ownersResponse.cards;
              this.principals = principalsResponse.cards;
            }).catch((err) => {
              console.error('Failed to load vessel owners/principals:', err);
            });
          })
          .catch((err) => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
      async populateCards(field, card_type) {
        try {
          const response = await this.$api.cards.index(card_type);
          this[field] = response.cards;
        } catch (err) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
        }
      },
      // preserveForm(value) {
      //   let eta = null;
      //   if (value.eta && moment(value.eta).isValid()) {
      //     eta = moment(value.eta).format("YYYY-MM-DD HH:mm");
      //   }

      //   let etd = null;
      //   if (value.etd && moment(value.etd).isValid()) {
      //     etd = moment(value.etd).format("YYYY-MM-DD HH:mm");
      //   }

      //   this.$set(this.operationBaseFieldsForm, 'eta', eta);
      //   this.$set(this.operationBaseFieldsForm, 'etd', etd);
      // },
      buildVesselInfoFormFromCaptainDetail(raw) {
        const base = JSON.parse(JSON.stringify(this.tmpFormVessel));
        if (!raw) return base;

        const vesselInfo = {
          ...base,
          ...raw,
          anchorage: { ...base.anchorage, ...(raw.anchorage || {}) },
          arrival: { ...base.arrival, ...(raw.arrival || {}) },
          departure: { ...base.departure, ...(raw.departure || {}) },
        };

        vesselInfo.eta_next_port = this.parseDateValue(vesselInfo.eta_next_port);
        vesselInfo.anchorage.arrival_bearth = this.parseDateValue(
          vesselInfo.anchorage.arrival_bearth || vesselInfo.anchorage.arrival_berth
        );
        vesselInfo.anchorage.gangway_down = this.parseDateValue(vesselInfo.anchorage.gangway_down);
        vesselInfo.anchorage.sail_berth = this.parseDateValue(vesselInfo.anchorage.sail_berth);

        return vesselInfo;
      },
      applyPrimaryBerthingFieldsFromAnchorage(anchorage = {}) {
        const arrivalBearth = this.parseDateValue(
          anchorage.arrival_bearth || anchorage.arrival_berth
        );
        const bearthingStation =
          anchorage.bearthing_station || anchorage.berthing_station || '';

        this.$set(this.operationBaseFieldsForm, 'arrival_bearth', arrivalBearth);
        this.$set(this.operationBaseFieldsForm, 'bearthing_station', bearthingStation);
      },
      async showVesselInformation(value) {
        if (!value || !value.id) return;
        try {
          const response = await this.$api.operation_captain_details.show(value.id);
          if (response.operation_captain_detail) {
            const vesselInfo = this.buildVesselInfoFormFromCaptainDetail(
              response.operation_captain_detail
            );

            this.applyPrimaryBerthingFieldsFromAnchorage(vesselInfo.anchorage);
            this.vesselInfoForm = vesselInfo;
          } else {
            this.vesselInfoForm = Object.assign({}, this.tmpFormVessel);
            this.applyPrimaryBerthingFieldsFromAnchorage();
          }
        } catch (err) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
        }
      },
      parseDateValue(dateValue) {
        if (!dateValue || dateValue === '' || dateValue === 'null' || dateValue === 'undefined') {
          return null;
        }

        if (dateValue instanceof Date) {
          return !isNaN(dateValue.getTime()) ? dateValue : null;
        }

        const value = String(dateValue).trim();
        const apiMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
        if (apiMatch) {
          const [, yyyy, mm, dd, hh, min] = apiMatch;
          const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min));
          return !isNaN(date.getTime()) ? date : null;
        }

        const date = new Date(dateValue);
        return !isNaN(date.getTime()) ? date : null;
      },
      applyOperationIdsToForm(operation) {
        if (!operation) return;
        if (operation.vessel_id != null && operation.vessel_id !== '') {
          this.$set(this.operationBaseFieldsForm, 'vessel_id', Number(operation.vessel_id));
        }
        if (operation.card_id != null && operation.card_id !== '') {
          this.$set(this.operationBaseFieldsForm, 'card_id', Number(operation.card_id));
        }
        if (operation.supervisor_id != null && operation.supervisor_id !== '') {
          this.$set(this.operationBaseFieldsForm, 'supervisor_id', Number(operation.supervisor_id));
        }
      },
      seedDropdownItemsFromOperation(operation) {
        if (!operation) return;

        const vesselId = Number(operation.vessel_id);
        if (vesselId && operation.vessel_name) {
          const hasVessel = this.vessels.some((v) => Number(v.id) === vesselId);
          if (!hasVessel) {
            this.vessels.push({ id: vesselId, name: operation.vessel_name });
          }
        }

        const cardId = Number(operation.card_id);
        if (cardId && operation.customer_name) {
          const hasOwner = this.owners.some((o) => Number(o.id) === cardId);
          if (!hasOwner) {
            this.owners.push({ id: cardId, name: operation.customer_name });
          }
        }

        const supervisorId = Number(operation.supervisor_id);
        if (supervisorId && operation.supervisor_name) {
          const hasUser = this.users.some((u) => Number(u.id) === supervisorId);
          if (!hasUser) {
            this.users.push({ id: supervisorId, name: operation.supervisor_name });
          }
        }
      },
      async handleOperationInit() {
        const initId = ++this.operationInitSeq;
        const value = this.operation;

        if (value.id || value.vessel_id) {
          this.operationBaseFieldsForm = Object.assign({}, this.tmpForm, value);
          this.$set(this.operationBaseFieldsForm, 'eta', this.parseDateValue(value.eta));
          this.$set(this.operationBaseFieldsForm, 'etd', this.parseDateValue(value.etd));
          this.applyOperationIdsToForm(value);
        } else {
          this.operationBaseFieldsForm = Object.assign({}, this.tmpForm);
          this.$refs.form && this.$refs.form.resetValidation();
          if (this.$refs.eta) this.$refs.eta.clearHandler();
          if (this.$refs.etd) this.$refs.etd.clearHandler();
        }

        await this.populateDropdown();
        if (initId !== this.operationInitSeq) return;

        if (value.id || value.vessel_id) {
          if (value.vessel_id) {
            await this.setCustomer(this.operationBaseFieldsForm.vessel_id);
            if (initId !== this.operationInitSeq) return;
          }
          this.seedDropdownItemsFromOperation(value);
          this.applyOperationIdsToForm(value);

          await this.showVesselInformation(value);
          if (initId !== this.operationInitSeq) return;
          await this.$nextTick();
        }
      },
    },
    watch: {
      operation(value) {
        this.handleOperationInit();
      },
      isDraft(value) {
        console.log('isDraft: ', value);
      },
    },
    created() {
      this.handleOperationInit();
    },
  };
</script>

<style scoped>
.form-disabled .v-input {
  pointer-events: none;
  opacity: 0.8;
}

.form-disabled .v-input__control {
  pointer-events: none;
  opacity: 0.8;
}

:deep .form-disabled .v-input__control .v-input__slot {
  pointer-events: none;
  opacity: 0.8;
}

:deep .form-disabled .v-input__control .v-input__slot .v-select__selection {
  pointer-events: none;
  opacity: 0.8;
}

:deep .form-disabled .v-input__control .v-input__slot input {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.8;
}

:deep .form-disabled .v-input__control .v-input__slot .v-textarea {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.8;
}

:deep .form-disabled .v-label {
  pointer-events: none;
  opacity: 0.8;
}

/* Exception: Allow billing party dropdown to be editable when status is Billing */
:deep .form-disabled .billing-party-field {
  pointer-events: auto !important;
  opacity: 1 !important;
}

:deep .form-disabled .billing-party-field .v-input__control {
  pointer-events: auto !important;
  opacity: 1 !important;
}

:deep .form-disabled .billing-party-field .v-input__control .v-input__slot {
  pointer-events: auto !important;
  opacity: 1 !important;
}

:deep .form-disabled .billing-party-field .v-input__control .v-input__slot .v-select__selection {
  pointer-events: auto !important;
  opacity: 1 !important;
  cursor: pointer !important;
}

:deep .form-disabled .billing-party-field .v-label {
  pointer-events: auto !important;
  opacity: 1 !important;
}

/* Same as billing party: allow PO Number edit when status is Billing and form is otherwise disabled */
:deep .form-disabled .po-number-field {
  pointer-events: auto !important;
  opacity: 1 !important;
}

:deep .form-disabled .po-number-field .v-input__control {
  pointer-events: auto !important;
  opacity: 1 !important;
}

:deep .form-disabled .po-number-field .v-input__control .v-input__slot {
  pointer-events: auto !important;
  opacity: 1 !important;
}

:deep .form-disabled .po-number-field .v-input__control .v-input__slot input {
  pointer-events: auto !important;
  cursor: text !important;
  opacity: 1 !important;
}

:deep .form-disabled .po-number-field .v-label {
  pointer-events: auto !important;
  opacity: 1 !important;
}
</style>